// ── SourcesChart — per-book line chart for the /sources page ───────
//
// One lightweight-charts panel with N line series (one per source) for
// the currently-filtered bet type / period / side. X-axis pinned to a
// rolling 30-minute window; Y-axis auto-scales so the band the lines
// cluster in stays readable rather than disappearing into a 0-100¢
// fixed scale.
//
// Data source is the same `sparklines` Map the table reads — the hub
// stores (ts, cents) buffers per line key, pruned to the last 30 min,
// so this component is a thin chart adapter over that state.
//
// Color per source: deterministic hash of source id → HSL hue so the
// same book stays the same color across renders + tab refreshes.
// Crosshair tooltips identify hovered book.

import { useEffect, useMemo, useRef } from 'react';
import {
  ColorType,
  CrosshairMode,
  LineSeries,
  LineStyle,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type Time,
} from 'lightweight-charts';
import type { SourcePoint } from '@/services/oddsStreamHub';

interface Props {
  /** Source ids in display order. The chart adds/removes series to
   *  match this set on each render. */
  sourceIds: number[];
  /** Look up the (ts, cents) buffer for a given source id. Returns
   *  empty for sources that haven't reported on the current filter. */
  pointsFor: (sourceId: number) => SourcePoint[];
  /** Bet type + period + side currently shown. Used as the chart
   *  remount key so a filter change clears stale series rather than
   *  trying to migrate them. */
  filterKey: string;
}

/** Visible x-axis window. Must match the hub's POINT_WINDOW_MS or be
 *  smaller (we can't render points the hub already pruned). 30 min
 *  matches the hub's default retention. */
const WINDOW_MS = 30 * 60 * 1000;

/** Stable per-source color. HSL hue from a small hash so books visually
 *  spread around the color wheel; saturation/lightness fixed for
 *  consistent contrast on a dark background. */
function colorForSource(sourceId: number): string {
  // Golden-ratio multiplier gives well-distributed hues across small
  // integer inputs without clustering.
  const hue = Math.floor((sourceId * 137.508) % 360);
  return `hsl(${hue} 70% 60%)`;
}

export function SourcesChart({ sourceIds, pointsFor, filterKey }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  // Per-source line series keyed by source id so we can update each
  // independently each render without rebuilding the whole chart.
  const seriesBySource = useRef(new Map<number, ISeriesApi<'Line'>>());

  // Build chart on mount; tear down on unmount. ResizeObserver keeps it
  // sized to the container so flexbox parents work.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const chart = createChart(el, {
      height: 280,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#9ca3af',
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: 'rgba(148,163,184,0.08)' },
        horzLines: { color: 'rgba(148,163,184,0.08)' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: {
        borderVisible: false,
        // Auto-scale to the actual band the books cluster in (~1-3¢
        // wide most of the time). A pinned 0-100 axis would render
        // every series as a flat invisible line in the noise floor.
        autoScale: true,
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: true,
      },
    });
    chartRef.current = chart;

    const resize = () => {
      const rect = el.getBoundingClientRect();
      chart.applyOptions({ width: Math.floor(rect.width) });
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(el);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesBySource.current.clear();
    };
  }, []);

  // Remount when the filter (bet type / period / side) changes —
  // simpler and cheaper than migrating ~30 series in place.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    for (const series of seriesBySource.current.values()) {
      chart.removeSeries(series);
    }
    seriesBySource.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Snapshot the source ids array so the effect deps are stable on
  // identity rather than re-running when the parent rebuilds the
  // array each render with the same contents.
  const sourceIdsKey = useMemo(
    () => sourceIds.slice().sort((a, b) => a - b).join(','),
    [sourceIds],
  );

  // Sync series set to the current source ids. Adds new series, removes
  // ones whose source has dropped out of the filter.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const seen = new Set<number>(sourceIds);
    // Add new
    for (const id of sourceIds) {
      if (!seriesBySource.current.has(id)) {
        const series = chart.addSeries(LineSeries, {
          color: colorForSource(id),
          lineWidth: 1,
          lastValueVisible: false,
          priceLineVisible: false,
          lineStyle: LineStyle.Solid,
          crosshairMarkerVisible: true,
        });
        seriesBySource.current.set(id, series);
      }
    }
    // Remove stale
    for (const [id, series] of seriesBySource.current.entries()) {
      if (!seen.has(id)) {
        chart.removeSeries(series);
        seriesBySource.current.delete(id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceIdsKey, filterKey]);

  // Push the latest points into each series. lightweight-charts requires
  // strictly-ascending unique time values per series; same-second
  // updates from a book would collide, so we de-dup by ts keeping the
  // latest sample for any colliding bucket.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const cutoff = Date.now() - WINDOW_MS;
    let hasCurrentPoints = false;
    for (const id of sourceIds) {
      const series = seriesBySource.current.get(id);
      if (!series) continue;
      const points = pointsFor(id);
      if (points.length === 0) {
        series.setData([]);
        continue;
      }
      // Bucket by integer second; keep the latest cents per bucket.
      // Filter to the visible window so older points don't widen the
      // x-axis past 30 min.
      const bySec = new Map<number, number>();
      for (const p of points) {
        if (!Number.isFinite(p.ts) || !Number.isFinite(p.cents) || p.ts < cutoff) continue;
        const sec = Math.floor(p.ts / 1000);
        bySec.set(sec, p.cents);
      }
      const data = Array.from(bySec.entries())
        .sort(([a], [b]) => a - b)
        .map(([sec, cents]) => ({ time: sec as Time, value: cents }));
      series.setData(data);
      if (data.length > 0) hasCurrentPoints = true;
    }
    // An empty chart has no time-to-index mapping. setVisibleRange throws
    // before data arrives (or after every point expires), crashing the page.
    if (!hasCurrentPoints) return;
    // Pin the visible range to the last 30 minutes regardless of
    // earliest point's actual ts — keeps the x-axis honest about what
    // the "30 min window" label means.
    const nowSec = Math.floor(Date.now() / 1000);
    chart.timeScale().setVisibleRange({
      from: (nowSec - WINDOW_MS / 1000) as Time,
      to: nowSec as Time,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceIdsKey, pointsFor, filterKey]);

  return (
    <div className="bg-card border-border rounded-lg border p-3">
      <div className="text-muted-foreground mb-2 flex items-center justify-between text-[11px]">
        <span className="font-semibold uppercase tracking-wider">
          Per-source movement
        </span>
        <span className="font-mono">
          last 30 min · {sourceIds.length} books · live
        </span>
      </div>
      <div ref={containerRef} className="h-[280px] w-full" />
    </div>
  );
}
